import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api, errText } from "../lib/api";
import { useStore } from "../context/Store";
import { t } from "../i18n";
import { CheckoutSummary } from "../components/CheckoutSummary";
import { ExpressPay } from "../components/ExpressPay";
import Seo from "../components/Seo";
import { useCountdown } from "../components/Countdown";
import { ChevronDown, ChevronLeft, Timer, CheckCircle2, AlertCircle } from "lucide-react";
import { fmtDate } from "../components/DateField";
import { countries, dialOf } from "../lib/countries";
import { cookieValue, tiktokIds } from "../components/MarketingLoader";
import { WINDOWS, emptyRecipient, withDial, validLocal, parseBuyerPhone, splitRecipientPhone, joinAddress,
         ANSWER_REQUIRED, today, tomorrow } from "../lib/checkoutHelpers";
import { dialFor } from "../components/checkout/dial";
import { ContactStep } from "../components/checkout/ContactStep";
import { GiftForm } from "../components/checkout/GiftForm";
import { PaymentStep } from "../components/checkout/PaymentStep";

export default function Checkout() {
  const { locale = "en" } = useParams();
  const [params] = useSearchParams();
  const { gifts, addGift, addGifts, removeGift, updateGift, currency, account, promoCode, boot } = useStore();
  const [step, setStep] = useState(1);
  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState([]);
  const [recipients, setRecipients] = useState({});
  const [buyer, setBuyer] = useState({ buyer_email: "", buyer_phone: "", buyer_country: "FR", discount_code: promoCode || "", whatsapp_opt_in: false, newsletter_opt_in: true });
  const [consent, setConsent] = useState(false);
  const [subOpts, setSubOpts] = useState(null);
  const [plan, setPlan] = useState("once");
  const [dates, setDates] = useState([]);
  const [openSummary, setOpenSummary] = useState(false);
  const [editingBasket, setEditingBasket] = useState(false);
  const [restored, setRestored] = useState(false);
  const [held, setHeld] = useState(false);
  const [expressHint, setExpressHint] = useState(false);
  const [locReqs, setLocReqs] = useState({});   // { [gift_id]: { token, url, copied } }
  const geoApplied = useRef(false);
  const prefillRan = useRef(false);
  const contactHydrated = useRef(false);
  const maps = boot?.maps || {};
  const holdToken = useRef(params.get("resume") || null);

  const countryList = useMemo(() => countries(locale), [locale]);
  const recurring = plan !== "once";
  const planDates = dates.filter(Boolean);
  const gateway = subOpts?.gateway === "tryb" ? "TRYB Billing" : "Stripe";
  const payTotal = quote ? quote.total.toFixed(2) : null;

  useEffect(() => {
    api.get("/subscription-options").then((r) => setSubOpts(r.data)).catch(() => {});
  }, []);

  // Guest checkout memory: restore contact details on refresh / back-navigation so the
  // buyer never re-types them. Logged-in buyers are prefilled from their account instead.
  useEffect(() => {
    if (!localStorage.getItem("fl_token")) {
      try {
        const c = JSON.parse(localStorage.getItem("fl_checkout_contact") || "null");
        if (c) setBuyer((b) => ({
          ...b,
          buyer_email: b.buyer_email || c.buyer_email || "",
          buyer_phone: b.buyer_phone || c.buyer_phone || "",
          buyer_country: c.buyer_country || b.buyer_country,
          whatsapp_opt_in: typeof c.whatsapp_opt_in === "boolean" ? c.whatsapp_opt_in : b.whatsapp_opt_in,
        }));
      } catch { /* ignore malformed cache */ }
    }
    contactHydrated.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist contact details as they're typed (kept for the customer's own convenience).
  useEffect(() => {
    if (!contactHydrated.current) return;
    try {
      localStorage.setItem("fl_checkout_contact", JSON.stringify({
        buyer_email: buyer.buyer_email, buyer_phone: buyer.buyer_phone,
        buyer_country: buyer.buyer_country, whatsapp_opt_in: buyer.whatsapp_opt_in,
      }));
    } catch { /* storage full / disabled — non-fatal */ }
  }, [buyer.buyer_email, buyer.buyer_phone, buyer.buyer_country, buyer.whatsapp_opt_in]);

  // geolocate the buyer's country for the phone dial-code default (skipped for bots server-side)
  useEffect(() => {
    if (geoApplied.current) return;
    api.get("/geo/detect").then((r) => {
      const cc = r.data?.country_code;
      geoApplied.current = true;
      if (r.data?.bot || !cc || !dialOf(cc)) return;
      import("../components/MarketingLoader").then((m) => m.setCorridor({ sender_country: cc })).catch(() => {});
      setBuyer((b) => (b.buyer_country === "FR" && !b.buyer_phone ? { ...b, buyer_country: cc } : b));
    }).catch(() => { geoApplied.current = true; });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // come back from a "finish your order" email
  useEffect(() => {
    const token = params.get("resume");
    if (!token) return;
    api.get(`/checkout/resume/${token}`).then(({ data }) => {
      const snap = data.snapshot || {};
      if (snap.buyer) setBuyer((b) => ({ ...b, ...snap.buyer }));
      if (snap.recipients) {
        // Normalize resume snapshots: legacy ones stored full "+<dial>…" numbers, new ones a local part + country.
        const norm = Object.fromEntries(Object.entries(snap.recipients).map(([k, v]) => {
          if (v.phone_country) return [k, v];
          const p = splitRecipientPhone(v.phone, countryList);
          const w = splitRecipientPhone(v.whatsapp, countryList);
          return [k, { ...v, phone: p.local, phone_country: p.country, whatsapp: w.local, whatsapp_country: w.local ? w.country : p.country }];
        }));
        setRecipients((r) => ({ ...norm, ...r }));
      }
      if (snap.plan) setPlan(snap.plan);
      if (snap.dates?.length) setDates(snap.dates);
      if (!gifts.length && snap.gifts?.length) snap.gifts.forEach((g) => addGift(g));
      setRestored(true);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Meta Commerce checkout URL: /{locale}/checkout?products=<id>:<qty>,<id>:<qty>&coupon=CODE
  // Meta reviews this URL periodically, so the basket must fill itself from the query.
  useEffect(() => {
    const list = params.get("products");
    if (!list || prefillRan.current) return;
    prefillRan.current = true;
    const coupon = params.get("coupon") || "";
    api.get(`/checkout/prefill?products=${encodeURIComponent(list)}&coupon=${encodeURIComponent(coupon)}&locale=${locale}&currency=${currency}`)
      .then(({ data }) => {
        const already = new Set(gifts.map((g) => g.product_id));
        const lines = [];
        (data.items || []).forEach((it) => {
          if (already.has(it.product_id)) return;
          for (let n = 0; n < it.quantity; n += 1) {
            lines.push({
              product_id: it.product_id, slug: it.slug, title: it.title, image: it.image,
              variant_name: it.variant_name, unit_price: it.price, currency, city: it.city,
              florist: { shop_name: "Fleurinelle" }, zone_id: it.zone_id, zone_name: it.zone_name,
              zone_fee: it.zone_fee, delivery_date: it.delivery_date, delivery_window: it.delivery_window,
              addons: [], card_message: "", card_language: locale,
              total: it.price + (it.zone_fee || 0), from_meta_catalog: true,
            });
          }
        });
        if (lines.length) addGifts(lines);
        if (data.coupon?.valid) setBuyer((b) => ({ ...b, discount_code: data.coupon.code }));
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (gifts.length !== 1 && plan !== "once") setPlan("once");
    if (plan === "custom_dates" && !dates.length && gifts[0]?.delivery_date) setDates([gifts[0].delivery_date]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan, gifts]);

  useEffect(() => {
    setRecipients((prev) => {
      const next = { ...prev };
      gifts.forEach((g) => {
        if (!next[g.gift_id]) next[g.gift_id] = { ...emptyRecipient };
      });
      return next;
    });
  }, [gifts]);

  useEffect(() => {
    if (!account) return;
    api.get("/account/recipients", { params: { locale } }).then((r) => setSaved(r.data)).catch(() => {});
    setBuyer((b) => {
      const next = { ...b, buyer_email: b.buyer_email || account.email };
      if (!b.buyer_phone && account.phone) {
        const parsed = parseBuyerPhone(account.phone, countryList);
        if (parsed) {
          next.buyer_phone = parsed.phone;
          if (parsed.country) next.buyer_country = parsed.country;
        }
      }
      return next;
    });
  }, [account, locale, countryList]);

  const setRec = (giftId, k, v) => setRecipients((prev) => ({ ...prev, [giftId]: { ...prev[giftId], [k]: v } }));
  const pickSaved = (g, s) => {
    const sp = splitRecipientPhone(s.phone, countryList);
    const sw = splitRecipientPhone(s.whatsapp, countryList);
    setRecipients((prev) => ({
      ...prev,
      [g.gift_id]: { ...prev[g.gift_id], name: s.name, phone: sp.local, phone_country: sp.country,
                     whatsapp: sw.local, whatsapp_country: sw.local ? sw.country : sp.country,
                     address_line1: s.address || "", apartment: "", address_line2: "", address: s.address, landmark: s.landmark_note },
    }));
  };

  // A code can arrive after mount (?code= on a recovery link is read by the store) — adopt it once.
  useEffect(() => {
    if (promoCode) setBuyer((b) => (b.discount_code ? b : { ...b, discount_code: promoCode }));
  }, [promoCode]);

  const askRecipient = async (g, r) => {
    try {
      const { data } = await api.post("/geo/location-requests", {
        recipient_name: r?.name || "",
        city: g.city?.name || "",
        origin_url: window.location.origin,
        locale,
      });
      setRec(g.gift_id, "location_request_token", data.token);
      setLocReqs((m) => ({ ...m, [g.gift_id]: { token: data.token, url: data.url, copied: false } }));
    } catch (e) { /* silent — buyer can retry */ }
  };

  // Native share sheet (WhatsApp, SMS, anything installed) with a clipboard fallback on desktop.
  const shareLocationLink = async (g, url) => {
    const text = `${t(locale, "deliverIntro")} ${url}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "Fleurinelle", text, url });
        return;
      }
    } catch (_) { /* user dismissed the sheet */ }
    await copyLocationLink(g, url);
  };
  // Only show "Copied" when the clipboard write actually succeeded (permission can be denied).
  const copyLocationLink = async (g, url = locReqs[g.gift_id]?.url) => {
    try {
      await navigator.clipboard.writeText(url);
      setLocReqs((m) => ({ ...m, [g.gift_id]: { ...m[g.gift_id], copied: true } }));
    } catch (_) { /* clipboard unavailable — the link stays selectable in the field */ }
  };

  const legs = useCallback((forQuote) =>
    gifts.map((g) => {
      const r = recipients[g.gift_id] || emptyRecipient;
      const unknownAddress = r.knows_address === false;
      const addressText = unknownAddress ? (r.landmark || "").trim() : joinAddress(r);
      const pDial = dialFor(r.phone_country);
      const wDial = dialFor(r.whatsapp_country || r.phone_country);
      return {
        items: [{ product_id: g.product_id, variant_name: g.variant_name, quantity: 1 }],
        addons: (g.addons || []).map((a) => ({ addon_id: a.id, quantity: a.qty })),
        recipient: {
          name: r.name || (forQuote ? "Placeholder Name" : r.name),
          phone: forQuote && !validLocal(r.phone, pDial) ? "+212600000000" : withDial(r.phone, pDial),
          whatsapp: r.wa_same === false ? withDial(r.whatsapp, wDial) : withDial(r.phone, pDial),
          address: addressText || (forQuote ? "placeholder address" : ""),
          zone_id: g.zone_id,
          landmark_note: r.landmark,
          delivery_location: r.delivery_location || null,
          location_request_token: r.location_request_token || null,
        },
        delivery_date: g.delivery_date,
        delivery_window: g.delivery_window,
        card_message: g.card_message,
        card_language: g.card_language,
        sender_name: g.sender_name,
        is_anonymous: g.is_anonymous,
        // No address yet means we must ring the recipient — a surprise delivery is impossible.
        call_before_delivery: unknownAddress ? true : r.call_before_delivery,
      };
    }), [gifts, recipients]);

  const basePayload = useCallback((forQuote) => ({
    deliveries: legs(forQuote),
    buyer_email: buyer.buyer_email || "quote@fleurinelle.com",
    buyer_phone: withDial(buyer.buyer_phone, dialFor(buyer.buyer_country)),
    buyer_country: buyer.buyer_country,
    buyer_locale: locale,
    currency,
    discount_code: buyer.discount_code || null,
    whatsapp_opt_in: buyer.whatsapp_opt_in,
    newsletter_opt_in: buyer.newsletter_opt_in,
    origin_url: window.location.origin,
    fbp: cookieValue("_fbp"),
    fbc: cookieValue("_fbc"),
    ...tiktokIds(),
    oppref: cookieValue("__oppref"),
    obref: cookieValue("__obref"),
    consent_accepted: forQuote ? true : consent,
    consent_locale: locale,
    recurrence: recurring ? { plan, dates: plan === "custom_dates" ? planDates : [] } : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [legs, buyer, currency, locale, plan, planDates, recurring, consent]);

  useEffect(() => {
    if (!gifts.length) return;
    let active = true;
    setQuote(null); setQuoteError("");
    const timer = setTimeout(() => {
      api.post("/orders/quote", basePayload(true))
        .then((r) => { if (active) setQuote(r.data); })
        .catch(e => { if (active) setQuoteError(errText(e)); });
    }, 250);
    return () => { active = false; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gifts, recipients, currency, buyer.discount_code, buyer.buyer_email, buyer.buyer_country, plan, JSON.stringify(planDates)]);

  // keep the basket safe: if they wander off at payment we can email them back
  useEffect(() => {
    if (!gifts.length || !buyer.buyer_email.includes("@") || step < 2) return undefined;
    const id = setTimeout(() => {
      api.post("/checkout/hold", {
        buyer_email: buyer.buyer_email,
        locale,
        currency,
        recipient_name: recipients[gifts[0].gift_id]?.name || "",
        total_label: quote ? `${quote.symbol} ${quote.total}` : "",
        origin_url: window.location.origin,
        snapshot: { gifts, recipients, buyer, plan, dates },
      }).then(({ data }) => {
        holdToken.current = data.token;
        setHeld(true);
      }).catch(() => {});
    }, 3000);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gifts, recipients, buyer, plan, dates, step, quote?.total]);

  const cityTiming = useMemo(() => {
    const first = gifts[0];
    if (!first) return null;
    const city = (boot?.cities || []).find((c) => c.id === first.city?.id);
    if (!city) return null;
    const sd = city.same_day;
    const win = sd?.windows?.find((w) => w.key === first.delivery_window);
    const openWins = (sd?.windows || []).filter((w) => w.open);
    return { name: first.city?.name || city.name, sameDay: first.delivery_date === today(),
             anyOpen: sd ? sd.any_open : city.timing?.same_day_open,
             winOpen: win ? win.open : city.timing?.same_day_open,
             winSeconds: win ? win.seconds_remaining : city.timing?.seconds_remaining,
             openLabels: openWins.map((w) => t(locale, w.key).split(" ")[0]) };
  }, [gifts, boot, locale]);
  const cutoff = useCountdown(cityTiming?.sameDay && cityTiming?.winOpen ? cityTiming.winSeconds : 0);

  // Same-day cut-off enforcement: earliest selectable date is tomorrow only when EVERY window is closed for that city.
  const cityFor = useCallback((g) => (boot?.cities || []).find((x) => x.id === g?.city?.id), [boot]);
  const anyWindowOpen = (c) => (c?.same_day ? c.same_day.any_open : c?.timing?.same_day_open);
  const windowOpenToday = (c, key) => {
    if (!c?.same_day) return c?.timing?.same_day_open !== false;
    const w = c.same_day.windows.find((x) => x.key === key);
    return w ? w.open : true;
  };
  const windowBlackedOut = (g, key) => (g?.blackout_windows?.[g?.delivery_date] || []).includes(key);
  const windowSelectable = (g, key) => {
    if (windowBlackedOut(g, key)) return false;
    if (g?.delivery_date === today() && !windowOpenToday(cityFor(g), key)) return false;
    return true;
  };
  const minDateFor = useCallback((g) => {
    const c = cityFor(g);
    return c && !anyWindowOpen(c) ? tomorrow() : today();
  }, [cityFor]);
  useEffect(() => {
    if (!boot?.cities) return;
    gifts.forEach((g) => {
      const c = boot.cities.find((x) => x.id === g?.city?.id);
      const openToday = c?.same_day ? c.same_day.any_open : c?.timing?.same_day_open;
      if (c && openToday === false && g.delivery_date && g.delivery_date <= today()) {
        updateGift(g.gift_id, { delivery_date: tomorrow() });
        return;
      }
      if (!windowSelectable(g, g.delivery_window)) {
        const open = WINDOWS.find((w) => windowSelectable(g, w));
        if (open) updateGift(g.gift_id, { delivery_window: open });
      }
    });
  }, [boot, gifts, updateGift]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!gifts.length)
    return (
      <div className="max-w-content mx-auto px-6 py-28 text-center" data-testid="checkout-empty">
        <Seo title={`${t(locale, "emptyBasket")} | Fleurinelle`} noindex />
        <h1 className="font-display text-3xl text-ink">{t(locale, "emptyBasket")}</h1>
        {params.get("resume") ? (
          <p className="text-sm text-muted mt-4" data-testid="resume-expired">{t(locale, "resumeExpired")}</p>
        ) : null}
        <Link to={`/${locale}`} className="btn-primary inline-block mt-8">{t(locale, "findFlowers")}</Link>
      </div>
    );

  const emailValid = /\S+@\S+\.\S+/.test(buyer.buyer_email);
  const answerRequired = ANSWER_REQUIRED[locale] || ANSWER_REQUIRED.en;
  const giftErrors = (g) => {
    const r = recipients[g.gift_id] || {};
    const unknownAddress = r.knows_address === false;
    return {
      name: r.name?.length > 1 ? "" : t(locale, "requiredName"),
      phone: validLocal(r.phone, dialFor(r.phone_country))
        ? ""
        : t(locale, dialFor(r.phone_country) === "+212" ? "requiredPhone" : "requiredPhoneIntl"),
      whatsapp: r.wa_same === false && !validLocal(r.whatsapp, dialFor(r.whatsapp_country || r.phone_country))
        ? t(locale, dialFor(r.whatsapp_country || r.phone_country) === "+212" ? "requiredPhone" : "requiredPhoneIntl")
        : "",
      address: unknownAddress
        ? ((r.landmark || "").trim().length > 4 ? "" : t(locale, "neighbourhoodRequired"))
        : (joinAddress(r).length > 4 ? "" : t(locale, "requiredAddress")),
      // The buyer must answer both questions before we can take the money.
      answers: r.knows_address === undefined || r.wa_same === undefined ? " " : "",
    };
  };
  const deliveryValid = gifts.every((g) => Object.values(giftErrors(g)).every((v) => !v));

  const pay = async () => {
    setBusy(true);
    setError("");
    try {
      try {
        const { trackBeginCheckout, setCorridor, identifyBuyer } = await import("../components/MarketingLoader");
        setCorridor({ sender_country: buyer.buyer_country, recipient_city: gifts[0]?.city?.name });
        await identifyBuyer({ email: buyer.buyer_email, phone: withDial(buyer.buyer_phone, dialFor(buyer.buyer_country)) });
        trackBeginCheckout({
          currency,
          total: quote?.total,
          total_mad: gifts.reduce((s, g) => s + (g.unit_price_mad || g.unit_price || 0), 0),
          buyer_email: buyer.buyer_email, buyer_country: buyer.buyer_country,
          buyer_phone: withDial(buyer.buyer_phone, dialFor(buyer.buyer_country)),
          recipient_city: gifts[0]?.city?.name,
          items: gifts.map((g) => ({ product_id: g.product_id, title: g.title,
                                     price_mad: g.unit_price_mad || g.unit_price, quantity: 1 })),
        });
      } catch (_) {}
      const { data } = await api.post("/orders", basePayload(false));
      try {
        const { trackPlaceOrder } = await import("../components/MarketingLoader");
        trackPlaceOrder({ currency, total: quote?.total, order_numbers: data.order_numbers,
                          items: gifts.map((g) => ({ product_id: g.product_id, title: g.title,
                                                     price_mad: g.unit_price_mad || g.unit_price, quantity: 1 })) });
      } catch (_) {}
      localStorage.setItem("fl_last_order", JSON.stringify({ order_numbers: data.order_numbers, email: buyer.buyer_email,
                                                             session_id: data.session_id, checkout_url: data.checkout_url,
                                                             at: Date.now() }));
      window.location.href = data.checkout_url;
    } catch (e) {
      setError(errText(e));
      setBusy(false);
    }
  };

  const steps = [t(locale, "stepContact"), t(locale, "stepDelivery"), t(locale, "stepPay")];

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-white" data-testid="checkout-page">
      <Seo title={`${t(locale, "orderSummary")} | Fleurinelle`} noindex />
      {/* mobile summary */}
      <div className="lg:hidden sticky top-0 z-40 bg-plaster border-b border-brass-light">
        <button className="flex justify-between items-center gap-4 w-full px-5 py-4 text-sm text-ink"
                data-testid="mobile-summary-toggle" onClick={() => setOpenSummary((v) => !v)}>
          <span className="flex items-center gap-2">
            {openSummary ? t(locale, "hideSummary") : t(locale, "showSummary")}
            <ChevronDown size={15} className={`transition-transform ${openSummary ? "rotate-180" : ""}`} />
          </span>
          <span className="text-xl font-medium tabular" data-testid="checkout-summary-total" aria-busy={!quote}>{quote ? `${quote.symbol} ${payTotal}` : "…"}</span>
        </button>
        {openSummary ? (
          <div className="px-5 pb-6 border-t border-brass-light/60" data-testid="mobile-summary-body">
            <CheckoutSummary locale={locale} gifts={gifts} quote={quote} code={buyer.discount_code}
                             setCode={(v) => setBuyer((b) => ({ ...b, discount_code: v }))}
                             codeApplied={!!quote?.discount_code} recurring={recurring}
                             editing onRemove={removeGift}
                             planDiscount={subOpts?.discount_percent} compact />
          </div>
        ) : null}
      </div>

      <div className="w-full lg:w-[55%] ps-5 pe-5 lg:ps-[8%] lg:pe-14 py-10 lg:py-16">
        <div className="flex items-center justify-between gap-4">
          <Link to={`/${locale}`} aria-label="Fleurinelle" data-testid="checkout-logo" className="inline-flex items-center">
            <img
              src="/brand/fleurinelle-header-light.png"
              alt="Fleurinelle"
              width="1200"
              height="360"
              className="h-8 md:h-9 w-auto block select-none"
              draggable="false"
            />
          </Link>
          <Link to={`/${locale}`} className="btn-ghost text-xs inline-flex items-center gap-1" data-testid="return-to-store">
            <ChevronLeft size={13} /> {t(locale, "returnToStore")}
          </Link>
        </div>

        <div className="mt-10">
          <ExpressPay
            locale={locale}
            gateway={gateway}
            busy={busy}
            ready={emailValid && deliveryValid}
            onPay={pay}
            onNeedInfo={() => {
              // Decide the correct step + the first field to focus
              const targetStep = emailValid ? 2 : 1;
              setStep(targetStep);
              setExpressHint(true);
              // Find the first invalid input testid
              let targetTestid = "buyer-email";
              if (emailValid) {
                for (let i = 0; i < gifts.length && targetTestid === "buyer-email"; i++) {
                  const r = recipients[gifts[i].gift_id] || {};
                  const e = giftErrors(gifts[i]);
                  if (e.name) targetTestid = `recipient-name-${i}`;
                  else if (e.phone) targetTestid = `recipient-phone-${i}`;
                  else if (r.wa_same === undefined) targetTestid = `wa-yes-${i}`;
                  else if (e.whatsapp) targetTestid = `recipient-whatsapp-${i}`;
                  else if (r.knows_address === undefined) targetTestid = `address-known-${i}`;
                  else if (e.address) targetTestid = r.knows_address === false ? `recipient-landmark-${i}` : `recipient-address-line1-${i}`;
                }
              }
              // Scroll the callout into view, then focus the empty field after the scroll settles
              requestAnimationFrame(() => {
                const banner = document.querySelector('[data-testid="express-hint"]');
                banner?.scrollIntoView({ behavior: "smooth", block: "center" });
                setTimeout(() => {
                  const el = document.querySelector(`[data-testid="${targetTestid}"]`);
                  if (el) {
                    el.scrollIntoView({ behavior: "smooth", block: "center" });
                    el.focus?.({ preventScroll: true });
                  }
                }, 350);
              });
            }}
          />
        </div>
        {expressHint && !(emailValid && deliveryValid) ? (
          <div
            role="alert"
            data-testid="express-hint"
            className="mb-8 -mt-2 flex items-start gap-3 rounded-sm border border-brass/60 bg-brass/10 px-4 py-3 text-ink"
          >
            <AlertCircle size={18} className="mt-0.5 shrink-0 text-brass" />
            <p className="text-sm leading-snug">{t(locale, "expressNeedInfo")}</p>
          </div>
        ) : null}

        <ol className="flex items-center gap-3 mb-10 text-xs" data-testid="checkout-breadcrumbs">
          {steps.map((s, i) => (
            <li key={s} className="flex items-center gap-3">
              <button type="button" data-testid={`checkout-step-${i + 1}`} disabled={i + 1 > step}
                      onClick={() => setStep(i + 1)}
                      className={`smallcaps transition-colors ${step === i + 1 ? "text-ink" : "text-muted hover:text-ink disabled:hover:text-muted"}`}>
                {step > i + 1 ? <CheckCircle2 size={12} className="inline me-1 text-brass" /> : `${i + 1}. `}{s}
              </button>
              {i < steps.length - 1 ? <span className="text-brass-light">/</span> : null}
            </li>
          ))}
        </ol>

        {restored ? (
          <p className="mb-8 text-xs text-success border-s-2 border-success ps-3" data-testid="basket-restored">
            {t(locale, "basketRestored")}
          </p>
        ) : null}

        {cityTiming ? (
          <p className="mb-8 text-xs text-ink bg-plaster border border-brass-light px-4 py-3 flex items-center gap-2"
             data-testid="cutoff-banner">
            <Timer size={14} className="text-brass shrink-0" />
            {cityTiming.sameDay && cityTiming.winOpen
              ? <span>{t(locale, "cutoffLead")} <span className="tabular text-ink">{cutoff.label}</span> {t(locale, "cutoffTail").replace("{city}", cityTiming.name)}</span>
              : cityTiming.sameDay && cityTiming.anyOpen
                ? <span>{t(locale, "cutoffTail").replace("{city}", cityTiming.name)} · {cityTiming.openLabels.join(" · ")}</span>
                : <span>{cityTiming.sameDay ? t(locale, "cutoffClosedLead").replace("{city}", cityTiming.name) : `${t(locale, "deliveryDate")}: ${fmtDate(gifts[0].delivery_date)} · ${cityTiming.name}`}</span>}
          </p>
        ) : null}

        {step === 1 ? (
          <ContactStep locale={locale} account={account} buyer={buyer} setBuyer={setBuyer} emailValid={emailValid}
                       countryList={countryList} onContinue={() => setStep(2)} />
        ) : null}

        {step === 2 ? (
          <section className="space-y-8" data-testid="checkout-recipient">
            <h1 className="font-display text-3xl text-ink">
              {t(locale, "deliveryTitle")}
              {gifts.length > 1 ? ` · ${gifts.length} ${t(locale, "deliveriesInBasket")}` : ""}
            </h1>
            {gifts.map((g, idx) => (
              <GiftForm key={g.gift_id} locale={locale} idx={idx} g={g}
                        r={recipients[g.gift_id] || emptyRecipient} errs={giftErrors(g)}
                        matching={saved.filter((s) => s.city_id === g.city?.id)}
                        countryList={countryList} maps={maps} answerRequired={answerRequired}
                        locReq={locReqs[g.gift_id]} setRec={setRec} updateGift={updateGift}
                        onPickSaved={(s) => pickSaved(g, s)} onRemove={() => removeGift(g.gift_id)}
                        onAsk={() => askRecipient(g, recipients[g.gift_id])}
                        onShare={() => shareLocationLink(g, locReqs[g.gift_id].url)}
                        onCopy={() => copyLocationLink(g)}
                        minDate={minDateFor(g)} windowSelectable={windowSelectable} />
            ))}
            <div className="flex flex-wrap gap-3 items-center">
              <button className="btn-ghost" data-testid="checkout-back-2" onClick={() => setStep(1)}>{t(locale, "back")}</button>
              <button data-testid="checkout-continue-2" className="btn-primary" disabled={!deliveryValid} onClick={() => setStep(3)}>
                {t(locale, "continue")}
              </button>
            </div>
          </section>
        ) : null}

        {step === 3 ? (
          <PaymentStep locale={locale} gateway={gateway} error={error || quoteError} consent={consent} setConsent={setConsent}
                       recurrence={{ subOpts, giftCount: gifts.length, plan, setPlan, dates, setDates, minDate: minDateFor(gifts[0]) }}
                       canPay={!busy && !!quote && !quoteError && emailValid && deliveryValid && consent && !(plan === "custom_dates" && !planDates.length)}
                       busy={busy} quote={quote} payTotal={payTotal} recurring={recurring}
                       onPay={pay} onBack={() => setStep(2)} held={held} />
        ) : null}
      </div>

      <aside className="hidden lg:block w-[45%] bg-plaster border-s border-brass-light ps-14 pe-[8%] py-16"
             data-testid="checkout-summary-column">
        <div className="sticky top-16 space-y-6">
          <div className="flex items-baseline justify-between">
            <h2 className="font-display text-xl text-ink">{t(locale, "orderSummary")}</h2>
            <button type="button" className="btn-ghost text-xs" data-testid="edit-basket"
                    onClick={() => setEditingBasket((v) => !v)}>
              {editingBasket ? t(locale, "basketDone") : t(locale, "editBasket")}
            </button>
          </div>
          <CheckoutSummary locale={locale} gifts={gifts} quote={quote} code={buyer.discount_code}
                           setCode={(v) => setBuyer((b) => ({ ...b, discount_code: v }))}
                           codeApplied={!!quote?.discount_code} recurring={recurring}
                           editing={editingBasket} onRemove={removeGift}
                           planDiscount={subOpts?.discount_percent} />
        </div>
      </aside>
    </div>
  );
}
